import type { ComponentChildren } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { cn } from '@/lib/cn';

export interface TabItem<T extends string = string> {
  value: T;
  label: ComponentChildren;
  count?: number;
  icon?: ComponentChildren;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  items: TabItem<T>[];
  variant?: 'underline' | 'pill';
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}

export function Tabs<T extends string = string>({
  value,
  onChange,
  items,
  variant = 'underline',
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: TabsProps<T>) {
  const [active, setActive] = useState(value);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const baseId = useId();

  useEffect(() => setActive(value), [value]);

  // Qué bordes tienen contenido fuera de la vista, para pintar el fundido.
  const listRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<{ start: boolean; end: boolean }>({
    start: false,
    end: false,
  });
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => {
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setOverflow((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [items.length]);

  const enabled = items.filter((it) => !it.disabled);

  function focusTab(v: T) {
    tabRefs.current[v]?.focus();
  }

  function onKeyDown(e: KeyboardEvent, idx: number) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    let nextIdx = idx;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % enabled.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + enabled.length) % enabled.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = enabled.length - 1;
    const next = enabled[nextIdx]!;
    setActive(next.value);
    onChange(next.value);
    focusTab(next.value);
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        // Scroll horizontal en lugar de wrap: en cualquier viewport, los tabs
        // viven en una única línea y se desliza si no caben. snap suave + scrollbar
        // oculta para sentirse nativo.
        'flex max-w-full snap-x snap-mandatory gap-1 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        variant === 'underline'
          ? 'border-border-subtle border-b'
          : 'border-border-subtle bg-surface-2 inline-flex rounded-lg border p-1',
        overflow.start && overflow.end
          ? 'scroll-fade-both'
          : overflow.end
            ? 'scroll-fade-end'
            : overflow.start
              ? 'scroll-fade-start'
              : null,
        className,
      )}
    >
      {items.map((it) => {
        const idxInEnabled = enabled.findIndex((e) => e.value === it.value);
        const isActive = it.value === active;
        return (
          <button
            key={it.value}
            ref={(el) => {
              tabRefs.current[it.value] = el;
            }}
            id={`${baseId}-tab-${it.value}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            // Sin aria-controls a propósito: TabPanel se renderiza desacoplado
            // (sin acceso a baseId ni id), así que el idref quedaba colgante
            // (axe `aria-valid-attr-value`, critical). El patrón sigue siendo
            // accesible vía role/aria-selected/navegación por teclado.
            tabIndex={isActive ? 0 : -1}
            disabled={it.disabled}
            onClick={() => {
              if (it.disabled) return;
              setActive(it.value);
              onChange(it.value);
            }}
            onKeyDown={(e) => onKeyDown(e, Math.max(0, idxInEnabled))}
            className={cn(
              'duration-med ease-snappy inline-flex shrink-0 cursor-pointer snap-start items-center gap-1.5 whitespace-nowrap transition-[colors,background,border-color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
              variant === 'underline'
                ? cn(
                    size === 'sm' ? 'h-7 px-2 text-xs' : 'text-13 h-8 px-3',
                    '-mb-px border-b-2',
                    isActive
                      ? 'text-fg-strong border-brand font-medium'
                      : 'text-fg-muted hover:text-fg hover:border-border border-transparent',
                    'focus-visible:shadow-focus rounded-t',
                  )
                : cn(
                    size === 'sm' ? 'h-7 px-2.5 text-xs' : 'text-13 h-8 px-3',
                    'rounded-md',
                    isActive
                      ? 'bg-surface text-fg-strong font-medium shadow-xs'
                      : 'text-fg-muted hover:bg-surface hover:text-fg',
                    'focus-visible:shadow-focus',
                  ),
            )}
          >
            {it.icon}
            <span className="truncate">{it.label}</span>
            {typeof it.count === 'number' && it.count > 0 ? (
              <span
                className={cn(
                  'text-3xs rounded-full px-1.5 font-medium tabular-nums',
                  isActive ? 'bg-brand-tint text-brand' : 'bg-surface-2 text-fg-faint',
                )}
              >
                {it.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  value: string;
  active: string;
  children: ComponentChildren;
  className?: string;
}

export function TabPanel({ value, active, children, className }: TabPanelProps) {
  if (value !== active) return null;
  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  );
}
